/* RISI Smart Cart Modal — post-add confirmation + audited compatibility
   recommendations. 2026-09-03. DEV feature (theme 182653059395).

   Safety contract (see docs/risi-cart-cross-sell-audit.md §7):
   - Opens ONLY on the theme's own post-success events (on:cart:add /
     on:line-item:change, dispatched by assets/product-form.js strictly
     after Shopify confirmed the add). Never on click; never on
     on:cart:error — the theme's stock error UI is untouched.
   - Everything recommendation-related is try/caught; any failure
     degrades to a plain confirmation modal. The add already happened.
   - The ONLY change to stock behaviour is suppressing the cart
     drawer's post-add auto-open (its silent refresh is kept), and only
     while this element exists on the page (i.e. while the feature flag
     in snippets/risi-smart-cart-modal.liquid is true).
   - No fuzzy matching: recommendations come exclusively from the
     audited assets/risi-cart-recommendations.json, keyed by handle,
     with prices/images/availability always fetched live. */

(function () {
  'use strict';

  if (customElements.get('risi-cart-modal')) return;

  var money = (typeof Intl !== 'undefined' && Intl.NumberFormat)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP' })
    : null;
  function fmt(pence) {
    var n = pence / 100;
    return money ? money.format(n) : ('£' + n.toFixed(2));
  }

  /* ---- VAT: mirror the VAT & Tax Switcher app's own state. Store
     prices (and all /cart.js figures) are INC VAT; the app derives the
     ex-VAT display. We derive the same ratio from the app's own output
     (displayed/original on any .finalPrice) and cache it — never a
     hard-coded rate. Unknown ratio → show inc-VAT figures (correct by
     construction, just the other mode). ---- */
  function vatMode() {
    var cb = document.querySelector('.tax-switcher input[type="checkbox"]');
    if (!cb) return 'inc';
    return cb.checked ? 'inc' : 'ex';
  }
  function learnExRatio() {
    try {
      var el = document.querySelector('.finalPrice[data-original-price]');
      if (!el) return;
      var orig = parseFloat(String(el.getAttribute('data-original-price')).replace(/[^\d.]/g, ''));
      var disp = parseFloat((el.textContent.match(/[\d,]+(?:\.\d+)?/) || [''])[0].replace(/,/g, ''));
      if (orig > 0 && disp > 0 && disp < orig - 0.005) {
        var r = disp / orig;
        if (r > 0.5 && r < 1) sessionStorage.setItem('risi-vat-ex-ratio', String(r));
      }
    } catch (e) { /* non-fatal */ }
  }
  function exRatio() {
    var r = parseFloat(sessionStorage.getItem('risi-vat-ex-ratio'));
    return (r > 0.5 && r < 1) ? r : null;
  }
  function displayPrice(penceInc) {
    if (vatMode() === 'ex') {
      var r = exRatio();
      if (r) {
        // r is a displayed/original ratio, so it carries penny-rounding
        // noise (29.99/35.99 is not exactly 1/1.2). Snap the implied
        // divisor to a 0.5% grid and divide in integer maths so ex-VAT
        // figures match the VAT app's own to the penny (2026-09-17:
        // was showing £83.32 where the product page shows £83.33).
        var k = Math.round(200 / r);
        if (k > 200) return { text: fmt(Math.round((Math.round(penceInc) * 200) / k)), label: 'ex VAT' };
      }
    }
    return { text: fmt(pencInt(penceInc)), label: 'inc VAT' };
  }
  function pencInt(p) { return Math.round(p); }

  var escapeHtml = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  class RisiCartModal extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.panel = this.querySelector('[data-risi-scm-panel]');
      this.itemEl = this.querySelector('[data-risi-scm-item]');
      this.totalsEl = this.querySelector('[data-risi-scm-totals]');
      this.recsEl = this.querySelector('[data-risi-scm-recs]');
      this.recsListEl = this.querySelector('[data-risi-scm-recs-list]');
      this.liveEl = this.querySelector('[data-risi-scm-live]');

      this.productCache = {};
      this.recsData = null;
      this.qtyQueue = Promise.resolve();
      this.qtyTarget = null;

      var self = this;

      // Post-success interception (the only trigger).
      this.addHandler = function (evt) {
        try {
          if (evt.target && evt.target.closest && evt.target.closest('quick-add-drawer')) return; // v1: quick-add keeps its own inline confirmation
          var detail = evt.detail || {};
          if (!detail.cart || !detail.variantId) return;
          self.openFor(detail.cart, detail.variantId);
        } catch (e) { /* never interfere with the add */ }
      };
      document.addEventListener('on:cart:add', this.addHandler);
      document.addEventListener('on:line-item:change', this.addHandler);

      // Suppress ONLY the drawer's post-add auto-open while this feature
      // is on. Manual opens (cart icon) and silent refreshes untouched.
      customElements.whenDefined('cart-drawer').then(function () {
        var proto = customElements.get('cart-drawer').prototype;
        if (proto.risiPatched) return;
        proto.risiPatched = true;
        var orig = proto.renderContents;
        proto.renderContents = function (data, openDrawer) {
          var suppress = !!document.querySelector('risi-cart-modal');
          return orig.call(this, data, suppress ? false : openDrawer);
        };
      }).catch(function () { /* no drawer on page: nothing to patch */ });

      // Stale-state guards.
      document.addEventListener('on:bfcache:load-restore', function () { self.close(true); });

      this.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-risi-scm-close]')) self.close();
      });
      this.keyHandler = function (evt) { if (evt.key === 'Escape') self.close(); };

      learnExRatio();
    }

    /* ================= open / close ================= */

    openFor(cart, variantId) {
      var line = (cart.items || []).find(function (i) { return i.variant_id === variantId; });
      if (!line) return; // nothing to show — leave stock behaviour alone
      this.cart = cart;
      this.lineKey = line.key;
      this.line = line;
      this.opener = document.activeElement;

      learnExRatio();
      this.renderItem();
      this.renderTotals();
      this.recsEl.hidden = true;
      this.recsListEl.innerHTML = '';
      this.recControllers = [];

      if (this.hidden) {
        this.hidden = false;
        this.setAttribute('aria-hidden', 'false');
        this.lockScroll();
        if (window.trapFocus) window.trapFocus(this, this.querySelector('.risi-scm__close'));
        this.addEventListener('keyup', this.keyHandler);
      }
      this.announce('Added to basket: ' + this.line.product_title);

      // Belt-and-braces single-UX guarantee: the theme schedules its
      // drawer auto-open ~700ms after the add. The prototype patch
      // suppresses it; if that ever races (script order edge), close
      // the drawer the moment it appears — checked, not blind, so we
      // never disturb focus when nothing opened.
      var selfGuard = this;
      setTimeout(function () {
        if (selfGuard.hidden) return;
        var drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.getAttribute('open') !== null) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
          if (window.trapFocus) window.trapFocus(selfGuard, selfGuard.querySelector('.risi-scm__close'));
        }
      }, 950);

      var self = this;
      this.vatPoll = this.vatPoll || setInterval(function () {
        if (self.hidden) return;
        learnExRatio();
        var sig = vatMode() + ':' + (exRatio() || '');
        if (sig !== self.vatSig) { self.vatSig = sig; self.renderItem(); self.renderTotals(); self.refreshRecPrices(); }
      }, 600);
      this.vatSig = vatMode() + ':' + (exRatio() || '');

      this.loadRecommendations(line).catch(function () { /* recs are enhancement only */ });
    }

    close(silent) {
      if (this.hidden) return;
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
      this.removeEventListener('keyup', this.keyHandler);
      if (window.removeTrapFocus) window.removeTrapFocus(this.opener && document.contains(this.opener) ? this.opener : null);
      if (!silent) this.announce('Dialog closed');
    }

    lockScroll() {
      this.scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = (-this.scrollY) + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, this.scrollY || 0); // Continue shopping preserves scroll
    }

    announce(msg) { if (this.liveEl) this.liveEl.textContent = msg; }

    /* ================= added item card ================= */

    variantText(line) {
      if (!line.options_with_values) {
        return (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '';
      }
      return line.options_with_values
        .filter(function (o) { return o.value && o.value !== 'Default Title'; })
        .map(function (o) { return escapeHtml(o.name) + ': <strong>' + escapeHtml(o.value) + '</strong>'; })
        .join(' &middot; ');
    }

    renderItem() {
      var line = this.line;
      var unit = displayPrice(line.final_price);
      var total = displayPrice(line.final_line_price);
      var img = line.featured_image && line.featured_image.url
        ? line.featured_image.url.replace(/(\.[a-z]{3,4})(\?|$)/i, '_160x$1$2') : null;

      this.itemEl.innerHTML =
        '<div class="risi-scm__item-media">' +
          (img ? '<img src="' + escapeHtml(img) + '" alt="" width="80" height="80" loading="lazy">' : '') +
        '</div>' +
        '<div class="risi-scm__item-info">' +
          '<p class="risi-scm__item-title">' + escapeHtml(line.product_title) + '</p>' +
          (this.variantText(line) ? '<p class="risi-scm__item-variant">' + this.variantText(line) + '</p>' : '') +
          (line.sku ? '<p class="risi-scm__item-sku">SKU: ' + escapeHtml(line.sku) + '</p>' : '') +
          '<p class="risi-scm__item-unit">' + unit.text + ' <span>' + unit.label + '</span></p>' +
        '</div>' +
        '<div class="risi-scm__item-buy">' +
          '<div class="risi-scm__qty" data-risi-scm-qty>' +
            '<button type="button" class="risi-scm__qty-btn" data-risi-qty-minus aria-label="Decrease quantity"' + (line.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
            '<span class="risi-scm__qty-val" aria-label="Quantity">' + line.quantity + '</span>' +
            '<button type="button" class="risi-scm__qty-btn" data-risi-qty-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<p class="risi-scm__item-total">' + total.text + ' <span>' + total.label + '</span></p>' +
          '<button type="button" class="risi-scm__remove" data-risi-remove>Remove</button>' +
        '</div>';

      var self = this;
      this.itemEl.querySelector('[data-risi-qty-minus]').addEventListener('click', function () { self.changeQty(-1); });
      this.itemEl.querySelector('[data-risi-qty-plus]').addEventListener('click', function () { self.changeQty(1); });
      this.itemEl.querySelector('[data-risi-remove]').addEventListener('click', function () { self.setQty(0); });
    }

    renderTotals() {
      var cart = this.cart;
      var sub = displayPrice(cart.items_subtotal_price);
      var count = cart.item_count;
      this.totalsEl.innerHTML =
        '<span>Basket subtotal <em>(' + count + (count === 1 ? ' item' : ' items') + ')</em></span>' +
        '<strong>' + sub.text + ' <span>' + sub.label + '</span></strong>';
    }

    /* ---- quantity: single serialized queue, latest-wins target,
       controls disabled in flight, cart LINE KEY not variant id. ---- */

    changeQty(delta) {
      // Accumulate from the PENDING target so rapid clicks stack
      // (2 -> +4 clicks -> 6, not four requests all targeting 3), and
      // clamp +/- to a floor of 1: reaching zero is only ever possible
      // through the explicit Remove control, never by mashing minus.
      var base = (this.qtyTarget !== null && this.qtyTarget !== undefined)
        ? this.qtyTarget : this.line.quantity;
      this.setQty(Math.max(1, base + delta));
    }

    /* Pump model: +/- stay clickable so rapid taps genuinely stack in
       qtyTarget; exactly one /cart/change request is in flight at a
       time, looping until the settled cart quantity equals the latest
       target (latest-wins, no race can misorder quantities). Remove is
       disabled while a change is in flight. */
    setQty(target) {
      this.qtyTarget = Math.max(0, target);
      this.setQtyBusy(true);
      this.pumpQty();
    }

    pumpQty() {
      var self = this;
      if (this.qtyPumping) return;
      this.qtyPumping = true;

      function settle() {
        self.qtyPumping = false;
        self.qtyTarget = null;
        self.setQtyBusy(false);
      }

      function step() {
        var want = self.qtyTarget;
        if (want === null || want === undefined || !self.line || want === self.line.quantity) {
          settle();
          return null;
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: self.lineKey, quantity: want })
        }).then(function (res) {
          if (!res.ok) throw new Error('cart change ' + res.status);
          return res.json();
        }).then(function (cart) {
          self.cart = cart;
          var line = cart.items.find(function (i) { return i.key === self.lineKey; });
          if (!line) { // removed via the explicit Remove control
            settle();
            self.announce('Removed from basket');
            self.syncStorefront();
            self.close();
            return null;
          }
          self.line = line;
          self.renderItem();
          self.renderTotals();
          self.refreshRecCards();
          self.announce('Basket updated: quantity ' + line.quantity);
          self.syncStorefront();
          return step(); // converge on the latest target
        });
      }

      Promise.resolve().then(step).catch(function () {
        settle();
        self.announce('Sorry — the basket could not be updated. Please try again.');
        self.showError('The basket could not be updated. Please try again.');
      });
    }

    setQtyBusy(busy) {
      var box = this.itemEl.querySelector('[data-risi-scm-qty]');
      if (box) box.classList.toggle('is-busy', !!busy);
      var remove = this.itemEl.querySelector('[data-risi-remove]');
      if (remove) { if (busy) remove.setAttribute('disabled', ''); else remove.removeAttribute('disabled'); }
      var minus = this.itemEl.querySelector('[data-risi-qty-minus]');
      if (minus && !busy && this.line && this.line.quantity <= 1) minus.setAttribute('disabled', '');
    }

    showError(msg) {
      var el = this.itemEl.querySelector('.risi-scm__error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'risi-scm__error';
        this.itemEl.appendChild(el);
      }
      el.textContent = msg;
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 4000);
    }

    /* Keep the rest of the storefront (icon bubble, drawer) in sync
       after modal-driven cart writes — using the theme's own patterns. */
    syncStorefront() {
      try {
        // Same assignment as the theme's own ProductForm.updateCartIcon.
        fetch('/?sections=cart-icon-bubble').then(function (r) { return r.json(); }).then(function (sections) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            bubble.innerHTML = sections['cart-icon-bubble'];
          }
        }).catch(function () {});
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } catch (e) { /* cosmetic only */ }
    }

    /* ================= recommendations ================= */

    fetchRecsData() {
      var self = this;
      if (this.recsData) return Promise.resolve(this.recsData);
      return fetch(this.dataset.recsUrl).then(function (r) {
        if (!r.ok) throw new Error('recs ' + r.status);
        return r.json();
      }).then(function (j) { self.recsData = j; return j; });
    }

    fetchProduct(handle) {
      var self = this;
      if (this.productCache[handle]) return Promise.resolve(this.productCache[handle]);
      return fetch('/products/' + handle + '.js').then(function (r) {
        if (!r.ok) throw new Error('product ' + r.status);
        return r.json();
      }).then(function (j) { self.productCache[handle] = j; return j; });
    }

    loadRecommendations(line) {
      var self = this;
      return this.fetchRecsData().then(function (data) {
        var src = data.sources && data.sources[line.handle];
        if (!src || !src.recs || !src.recs.length) return;

        var rules = src.recs.filter(function (r) {
          if (r.onlyVariantIds && r.onlyVariantIds.indexOf(line.variant_id) === -1) return false;
          if (r.handle === line.handle) return false;
          return true;
        }).sort(function (a, b) { return a.priority - b.priority; }).slice(0, 4);
        if (!rules.length) return;

        return Promise.all(rules.map(function (rule) {
          return self.fetchProduct(rule.handle).then(function (product) {
            if (!product || !product.available) return null;
            if (!product.price_min && product.price_min !== 0) return null;
            return { rule: rule, product: product };
          }).catch(function () { return null; });
        })).then(function (cards) {
          cards = cards.filter(Boolean);
          if (!cards.length) return;
          self.renderRecs(cards);
        });
      });
    }

    recQty(rule) {
      if (rule.qty && rule.qty.type === 'perSourceUnit') {
        return Math.max(1, rule.qty.factor * (this.line ? this.line.quantity : 1));
      }
      return (rule.qty && rule.qty.value) || 1;
    }

    /* ---- Recommendation cards (2026-09-03 rev 2, per merchant
       feedback): every card is fully self-service inside the modal —
       a quantity stepper on every card, and multi-variant products
       render their option dropdowns INLINE (price follows the chosen
       variant) instead of navigating away mid-checkout, which read as
       "did my item actually go in the basket?". After adding, the card
       flips to an "In basket" state whose stepper live-edits that cart
       line (same converging-pump pattern as the main item; minus at 1
       removes the line and the card returns to its Add state). The
       customer still explicitly chooses options — we never silently
       add a default variant of a multi-variant product. ---- */

    renderRecs(cards) {
      var self = this;
      this.recsListEl.innerHTML = '';
      this.recControllers = [];
      cards.forEach(function (card) { self.recControllers.push(self.buildRecCard(card)); });
      this.recsEl.hidden = false;
    }

    buildRecCard(card) {
      var self = this;
      var p = card.product;
      var img = p.featured_image ? String(p.featured_image).replace(/(\.[a-z]{3,4})(\?|$)/i, '_120x$1$2') : null;
      var multi = p.variants.length > 1;

      var el = document.createElement('div');
      el.className = 'risi-scm__rec';
      el.setAttribute('data-rec-handle', p.handle);

      var optsHtml = '';
      if (multi) {
        p.options.forEach(function (opt, oi) {
          optsHtml += '<label class="risi-scm__rec-opt"><span>' + escapeHtml(opt.name) + '</span>' +
            '<select data-rec-opt="' + oi + '">' +
            opt.values.map(function (v) { return '<option value="' + escapeHtml(v) + '">' + escapeHtml(v) + '</option>'; }).join('') +
            '</select></label>';
        });
      }

      el.innerHTML =
        '<div class="risi-scm__rec-media">' + (img ? '<img src="' + escapeHtml(img) + '" alt="" width="56" height="56" loading="lazy">' : '') + '</div>' +
        '<div class="risi-scm__rec-info">' +
          '<p class="risi-scm__rec-title">' + escapeHtml(p.title) + '</p>' +
          '<p class="risi-scm__rec-reason">' + escapeHtml(card.rule.reason) + '</p>' +
          optsHtml +
          '<p class="risi-scm__rec-price" data-rec-price></p>' +
        '</div>' +
        '<div class="risi-scm__rec-cta" data-rec-buy></div>';
      this.recsListEl.appendChild(el);

      var ctrl = {
        el: el,
        product: p,
        rule: card.rule,
        variant: null,
        qty: this.recQty(card.rule),
        userTouchedQty: false,
        pump: { target: null, running: false },
      };

      var selects = Array.prototype.slice.call(el.querySelectorAll('[data-rec-opt]'));

      ctrl.resolve = function () {
        if (!multi) { ctrl.variant = p.variants[0]; return; }
        var chosen = selects.map(function (s) { return s.value; });
        ctrl.variant = p.variants.find(function (v) {
          return v.options.every(function (val, i) { return val === chosen[i]; });
        }) || null;
      };

      ctrl.line = function () {
        if (!ctrl.variant) return null;
        return (self.cart.items || []).find(function (i) { return i.variant_id === ctrl.variant.id; }) || null;
      };

      ctrl.renderPrice = function () {
        var priceEl = el.querySelector('[data-rec-price]');
        if (ctrl.variant) {
          var pr = displayPrice(ctrl.variant.price);
          priceEl.innerHTML = pr.text + ' <span>' + pr.label + '</span>';
        } else {
          priceEl.innerHTML = '<span>Combination unavailable</span>';
        }
      };

      ctrl.renderBuy = function () {
        var buyEl = el.querySelector('[data-rec-buy]');
        var line = ctrl.line();
        if (line) {
          buyEl.innerHTML =
            '<span class="risi-scm__rec-incart">&#10003; In basket</span>' +
            '<div class="risi-scm__qty risi-scm__qty--mini">' +
              '<button type="button" class="risi-scm__qty-btn" data-rl-minus aria-label="Decrease quantity of ' + escapeHtml(p.title) + '">&minus;</button>' +
              '<span class="risi-scm__qty-val">' + line.quantity + '</span>' +
              '<button type="button" class="risi-scm__qty-btn" data-rl-plus aria-label="Increase quantity of ' + escapeHtml(p.title) + '">+</button>' +
            '</div>';
          buyEl.querySelector('[data-rl-minus]').addEventListener('click', function () { ctrl.changeLine(-1); });
          buyEl.querySelector('[data-rl-plus]').addEventListener('click', function () { ctrl.changeLine(1); });
        } else if (ctrl.variant && ctrl.variant.available) {
          buyEl.innerHTML =
            '<div class="risi-scm__qty risi-scm__qty--mini">' +
              '<button type="button" class="risi-scm__qty-btn" data-rq-minus aria-label="Decrease quantity to add">&minus;</button>' +
              '<span class="risi-scm__qty-val">' + ctrl.qty + '</span>' +
              '<button type="button" class="risi-scm__qty-btn" data-rq-plus aria-label="Increase quantity to add">+</button>' +
            '</div>' +
            '<button type="button" class="risi-scm__rec-add" data-rec-add>Add</button>';
          buyEl.querySelector('[data-rq-minus]').addEventListener('click', function () {
            ctrl.qty = Math.max(1, ctrl.qty - 1); ctrl.userTouchedQty = true; ctrl.renderBuy();
          });
          buyEl.querySelector('[data-rq-plus]').addEventListener('click', function () {
            ctrl.qty += 1; ctrl.userTouchedQty = true; ctrl.renderBuy();
          });
          buyEl.querySelector('[data-rec-add]').addEventListener('click', function () { ctrl.add(); });
        } else {
          buyEl.innerHTML = '<span class="risi-scm__rec-unavailable">Unavailable</span>';
        }
      };

      ctrl.add = function () {
        if (!ctrl.variant || !ctrl.variant.available) return;
        var btn = el.querySelector('[data-rec-add]');
        if (btn) { btn.setAttribute('disabled', ''); btn.classList.add('is-busy'); }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: ctrl.variant.id, quantity: ctrl.qty })
        }).then(function (res) {
          if (!res.ok) throw new Error('add ' + res.status);
          return fetch('/cart.js');
        }).then(function (res) { return res.json(); }).then(function (cart) {
          self.cart = cart;
          var mainLine = cart.items.find(function (i) { return i.key === self.lineKey; });
          if (mainLine) self.line = mainLine;
          self.renderTotals();
          ctrl.renderBuy();
          self.announce('Added to basket: ' + p.title);
          self.syncStorefront();
        }).catch(function () {
          if (btn) { btn.removeAttribute('disabled'); btn.classList.remove('is-busy'); }
          self.announce('Sorry — that item could not be added.');
        });
      };

      /* In-basket line editing: same converging pump as the main item,
         scoped to this card's cart line. Minus at quantity 1 removes
         the line and the card reverts to its Add state. */
      ctrl.changeLine = function (delta) {
        var line = ctrl.line();
        if (!line) return;
        var base = (ctrl.pump.target !== null) ? ctrl.pump.target : line.quantity;
        ctrl.pump.target = Math.max(0, base + delta);
        if (ctrl.pump.running) return;
        ctrl.pump.running = true;
        var key = line.key;

        function step() {
          var want = ctrl.pump.target;
          var cur = ((self.cart.items || []).find(function (i) { return i.key === key; }) || {}).quantity;
          if (want === null || want === cur) {
            ctrl.pump.running = false;
            ctrl.pump.target = null;
            return null;
          }
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: want })
          }).then(function (res) {
            if (!res.ok) throw new Error('change ' + res.status);
            return res.json();
          }).then(function (cart) {
            self.cart = cart;
            var mainLine = cart.items.find(function (i) { return i.key === self.lineKey; });
            if (mainLine) self.line = mainLine;
            self.renderTotals();
            ctrl.renderBuy();
            self.syncStorefront();
            var now = (cart.items.find(function (i) { return i.key === key; }) || {}).quantity;
            self.announce(now ? ('Basket updated: ' + p.title + ' quantity ' + now) : (p.title + ' removed from basket'));
            return step();
          });
        }
        Promise.resolve().then(step).catch(function () {
          ctrl.pump.running = false;
          ctrl.pump.target = null;
          ctrl.renderBuy();
          self.announce('Sorry — the basket could not be updated. Please try again.');
        });
      };

      selects.forEach(function (s) {
        s.addEventListener('change', function () {
          ctrl.resolve();
          ctrl.renderPrice();
          ctrl.renderBuy();
        });
      });

      // Initial state: preselect the first AVAILABLE variant's options
      // (visible and changeable — the customer still explicitly sees
      // what they are adding before pressing Add).
      if (multi) {
        var firstAvail = p.variants.find(function (v) { return v.available; }) || p.variants[0];
        selects.forEach(function (s, i) { s.value = firstAvail.options[i]; });
      }
      ctrl.resolve();
      ctrl.renderPrice();
      ctrl.renderBuy();
      return ctrl;
    }

    refreshRecPrices() {
      (this.recControllers || []).forEach(function (ctrl) { ctrl.renderPrice(); });
    }

    refreshRecCards() {
      var self = this;
      (this.recControllers || []).forEach(function (ctrl) {
        // Follow the pins-per-beam style formulas as the main quantity
        // changes, unless the customer has taken over the stepper.
        if (!ctrl.userTouchedQty && ctrl.rule.qty && ctrl.rule.qty.type === 'perSourceUnit') {
          ctrl.qty = self.recQty(ctrl.rule);
        }
        ctrl.renderBuy();
      });
    }
  }

  customElements.define('risi-cart-modal', RisiCartModal);
}());
